// @flow

// Types
import type { Styles } from '..'

// Constants
export const ANIMATION_TIME = '0.3s'
export const ANIMATION_CURVE = 'cubic-bezier(0.165, 0.84, 0.44, 1)'
export const DEFAULT_ANIMATION = `${ANIMATION_TIME} ${ANIMATION_CURVE}`

// Container for the input
export const Container = ({ $disabled }: Styles) => ({
  fontSize: '16px',
  display: 'block',
  position: 'relative',
  border: 0,
  padding: 0,
  minWidth: 0,
  maxWidth: '100%',
  boxSizing: 'border-box',
  pointerEvents: $disabled ? 'none' : 'initial',
  opacity: $disabled ? 0.5 : 1,
  backgroundColor: 'transparent',
  marginTop: '10px',
})

// The line above the input
export const Baseline = ({ $focus, $invalid, $palette }: Styles) => {
  let color = `${$palette('gray')}`
  if ($focus) color = `${$palette('primary')}`
  if ($invalid) color = `${$palette('red')}`

  return {
    width: '100%',
    height: '1px',
    backgroundColor: color,
    boxShadow: $focus ? `0 1px 0 ${color}` : 'none',
  }
}

// The input element
export const InputWrapper = ({ $haveLabel, $palette }: Styles) => {
  return {
    transition: `opacity ${DEFAULT_ANIMATION}`,
    marginTop: $haveLabel ? '16px' : 0,
    color: `${$palette('text')}`,
    display: 'flex',
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 0,
    lineHeight: '1.1875em',
    flexFlow: 'row nowrap',
    alignItems: 'center',
  }
}

// A small wrapper around content - input and label
export const Content = {
  maxWidth: '100%',
  display: 'flex',
  position: 'relative',
  flexFlow: 'column nowrap',
}

// Float label element
export const Label = ({
  $focus,
  $invalid,
  $nonEmpty,
  $disableFloatingLabel,
  $palette,
}: Styles) => {
  const activeStyles = !($focus || $nonEmpty || $disableFloatingLabel)
    ? {}
    : {
        color: `${$palette('text')}`,
        transform: 'translate(0, 1.5px) scale(0.85)',
        cursor: 'default',
      }
  if ($focus) activeStyles.color = `${$palette('primary')}`
  if ($invalid) activeStyles.color = `${$palette('red')}`

  return {
    display: 'block',
    position: 'absolute',
    width: '100%',
    left: '0',
    top: '0',
    color: `${$palette('text').lighten(15)}`,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    transformOrigin: '0 50%',
    lineHeight: '1.5em',
    textAlign: 'left',
    cursor: 'text',
    fontSize: '1em',
    // To make Label transparent for mouse events, so sibling elements, such
    // as InputWrapper, can catch clicks on Label (when user click to Label
    // the event will be triggered on InputWrapper instead of Label)
    pointerEvents: 'none',
    transform: 'scale(1) translate3d(0px, 24px, 0px)',
    transition: `color ${DEFAULT_ANIMATION}, transform ${DEFAULT_ANIMATION}`,
    ...activeStyles,
  }
}

export const InputWithControls = {
  display: 'flex',
  maxWidth: '100%',
  flexGrow: 1,
  alignItems: 'center',
  flexFlow: 'row nowrap',
  position: 'relative',
}

// Errors container
export const Errors = ({ $invalid, $palette }: Styles) => ({
  zIndex: 10,
  transition: `opacity ${DEFAULT_ANIMATION}`,
  opacity: $invalid ? 1 : 0,
  width: $invalid ? 'auto' : 0,
  height: $invalid ? 'auto' : 0,
  pointerEvents: 'none',
  userSelect: 'none',
  marginRight: '5px',
  maxWidth: '100%',
  textAlign: 'right',
  fontSize: '0.857em',
  transform: 'translateY(3px)',
  backgroundColor: 'rgba(255,255,255,0.6)',
  color: `${$palette('red')}`,
})

// Container for loading spinner to show/hide it with animation
export const LoadingContainer = {
  display: 'flex',
  pointerEvents: 'none',
  position: 'absolute',
  right: '7px',
}

// Button to clear the input
export const ClearButton = ({ $disabled, $nonEmpty, $palette }: Styles) => {
  const active = $nonEmpty && !$disabled
  return {
    pointerEvents: !active ? 'none' : 'initial',
    transition: `color ${DEFAULT_ANIMATION}, opacity ${DEFAULT_ANIMATION}`,
    cursor: 'pointer',
    opacity: !active ? 0 : 1,
    fontSize: '12px',
    padding: '8px 5px',
    color: `${$palette('gray')}`,
    ':hover': !active
      ? {}
      : {
          color: `${$palette('text')}`,
        },
    ':after': {
      content: '"✕"',
      lineHeight: '12px',
      display: 'block',
    },
  }
}

// Arrow button for selectable fields
export const ArrowButton = ({ $palette }: Styles) => ({
  transition: `opacity ${DEFAULT_ANIMATION}`,
  padding: '8px 5px',
  lineHeight: '12px',
  cursor: 'pointer',
  opacity: 0.6,
  ':hover': { opacity: 1 },
  ':after': {
    pointerEvents: 'none',
    content: '""',
    borderColor: `${$palette('text').lighten(45)} transparent transparent`,
    borderStyle: 'solid',
    borderWidth: '5px 5px 2.5px',
    display: 'inline-block',
    height: 0,
    width: 0,
  },
})

// Wrapper for the content before the input
export const PrefixWrapper = ({ $focus, $palette, $hidden }: Styles) => ({
  opacity: $hidden ? 0 : 1,
  transition: `opacity ${DEFAULT_ANIMATION}, color ${DEFAULT_ANIMATION}`,
  color: $focus ? `${$palette('primary')}` : 'currentColor',
  marginRight: '8px',
  flexShrink: 0,
})

// Wrapper for the content after the input
export const PostfixWrapper = {
  display: 'flex',
  flexFlow: 'row nowrap',
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: '8px',
  marginRight: '6px',
  flexShrink: 0,
}
